import {
  AllContent,
  AllFeatures,
  AllPrograms,
  RawOtpBundle,
  toProgramStateKind,
} from '../../common/bundle.types';
import { faAlignLeft, faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import {
  BundleItemType,
  BundleStatus,
  CheckoutPageType,
} from 'generated/graphql';
import { ColorsVars } from '@design-system/vars.css';
import {
  BundleCardItem,
  Submitting,
  BundlesVMDispatcher,
  Standard,
} from './otpBundles.viewModel';
import { ButtonProps } from '@design-system/components/button/button';
import { ButtonStylingProps } from '@design-system/components/button/helper';
import { collectionLinks } from '@client/new/pages/collections/collection-links';
import { EditBundleSubmit, NewBundleSubmit } from '../model/otpBundles.model';
import { isEmpty, noop } from 'lodash';
import { trackEvent } from '@services/tracking';
import { EditOtpBundleVMDispatcher } from './editOtpBundle.vm';
import {
  isProgramNotReady,
  toNotReadyTooltip,
} from '../../common/readinessGating';

export const statusColor = {
  ARCHIVED: {
    bg: 'ERROR-20' as ColorsVars,
    text: 'ERROR-100' as ColorsVars,
  },
  DRAFT: {
    bg: 'D-20' as ColorsVars,
    text: 'D-100' as ColorsVars,
  },
  PUBLISHED: {
    bg: 'G-80' as ColorsVars,
    text: 'W-100' as ColorsVars,
  },
};

const toProgramDetails = (
  allPrograms: AllPrograms,
  bundleItems?: RawOtpBundle['items'],
) => {
  const mappedPrograms = (bundleItems || [])
    .filter((item) => item.itemType === BundleItemType.Program)
    .map((item) => ({
      title: allPrograms.find((p) => p.id === item.itemId)?.name || '',
    }));

  return mapItemsToUi(mappedPrograms);
};

const toContentDetails = (
  allContent: AllContent,
  bundleItems?: RawOtpBundle['items'],
) => {
  const mappedContent = (bundleItems || [])
    .filter((item) => item.itemType === BundleItemType.Content)
    .map((item) => ({
      title: allContent.find((p) => p.id === item.contentId)?.name || '',
    }));

  return mapItemsToUi(mappedContent);
};

const mapItemsToUi = (items: Array<{ title: string }>) => {
  const result = [];
  const maxRowChars = 40;
  let currentLength = 0;
  let currentIndex = 0;

  for (; currentIndex < items.length; currentIndex++) {
    const item = items[currentIndex];
    if (currentLength + item.title.length > maxRowChars) {
      break;
    }
    result.push(item);
    currentLength += item.title.length;
  }

  if (currentIndex === items.length) return result;

  const remainingItems = items.slice(currentIndex).map((i) => i.title);

  return [
    ...result,
    { title: `+${items.length - currentIndex}`, tooltipItems: remainingItems },
  ];
};

export const toFormFeatures = (allFeatures: AllFeatures) =>
  allFeatures.map((f) => ({
    id: f.id,
    name: f.name,
  }));

export const toFormPrograms = (
  allPrograms: AllPrograms,
  wasPurchased: boolean,
  savedProgramIds: number[],
) =>
  allPrograms.map((p) => {
    const lockedToBundle = wasPurchased && savedProgramIds.includes(p.id);
    const notReady = isProgramNotReady(p.readiness);
    return {
      id: p.id,
      name: p.name,
      disabled: lockedToBundle || notReady,
      disabledTooltip: lockedToBundle
        ? undefined
        : notReady
          ? toNotReadyTooltip(p.readiness)
          : undefined,
      status: {
        state: toProgramStateKind(p),
        readiness: p.readiness ?? null,
      },
    };
  });

export const toFormSelectedPrograms = (
  allPrograms: AllPrograms,
  programs: number[],
  wasPurchased: boolean,
  savedProgramIds: number[],
  isFree: boolean,
) =>
  allPrograms
    .filter((p) => programs.includes(p.id))
    .map((p) => ({
      id: p.id,
      name: p.name,
      disableDelete: wasPurchased && !isFree && savedProgramIds.includes(p.id),
      // Status carries the same readiness payload as the option dropdown
      // so the picked-programs list renders the matching pill. Under the
      // legacy experience the readiness layer is suppressed at the data
      // boundary (here) so the View renders a plain Draft/Published pill.
      status: {
        state: toProgramStateKind(p),
        readiness: p.readiness ?? null,
      },
    }));

export const toFormContent = (
  allContent: AllContent,
  wasPurchased: boolean,
  savedContentIds: string[],
) =>
  allContent.map((c) => ({
    id: c.id,
    name: c.name,
    disabled: wasPurchased && savedContentIds.includes(c.id),
  }));

export const toFormSelectedContent = (
  allContent: AllContent,
  content: string[],
  wasPurchased: boolean,
  savedContentIds: string[],
  isFree: boolean,
) =>
  allContent
    .filter((c) => content.includes(c.id))
    .map((p) => ({
      id: p.id,
      name: p.name,
      disableDelete: wasPurchased && !isFree && savedContentIds.includes(p.id),
    }));

const toBudleDetails = (items?: RawOtpBundle['items']) => {
  const programsCount = (items || []).filter(
    (i) => i.itemType === BundleItemType.Program,
  ).length;

  const contentCount = (items || []).filter(
    (i) => i.itemType === BundleItemType.Content,
  ).length;

  if (programsCount > 0 && contentCount > 0)
    return `${programsCount} ${
      programsCount === 1 ? 'Program' : 'Programs'
    } • ${contentCount} ${contentCount === 1 ? 'Item' : 'Items'}`;
  if (programsCount > 0)
    return `${programsCount} ${programsCount === 1 ? 'Program' : 'Programs'}`;
  if (contentCount > 0)
    return `${contentCount} ${contentCount === 1 ? 'Item' : 'Items'}`;
  return '';
};

export const filterBundles = (filter: string, bundles: RawOtpBundle[]) => {
  switch (filter) {
    case 'All':
      return bundles;
    case 'Drafts':
      return bundles.filter((b) => b.status === BundleStatus.Draft);
    case 'Published':
      return bundles.filter((b) => b.status === BundleStatus.Published);
    case 'Archived':
      return bundles.filter((b) => b.status === BundleStatus.Archived);
    case 'Free':
      return bundles.filter((b) => b.isFree);
    default:
      return bundles;
  }
};

export const toOTPBundleItems = (
  otpBundles: RawOtpBundle[],
  allPrograms: AllPrograms,
  allFeatures: AllFeatures,
  allContent: AllContent,
  dispatch: BundlesVMDispatcher,
  submittedNewBundle: NewBundleSubmit,
  submittedEditBundle: EditBundleSubmit,
): Array<BundleCardItem> => {
  // Sort bundles by ID in descending order (newest first)
  const sortedBundles = [...otpBundles].sort((a, b) => b.id - a.id);

  const existingBundles = sortedBundles.map((bundle) => ({
    onClick: () => dispatch('editBundle', bundle.id),
    menu: [
      {
        icon: faAlignLeft,
        disabled: !bundle.description,
        tooltip: bundle.description,
      },
      {
        icon: faShareNodes,
        disabled: bundle.status !== BundleStatus.Published,
        onClick: () => dispatch('openLinksModal', bundle.id),
      },

      {
        icon: faCopy,
        disabled: false,
        onClick: () => dispatch('duplicateBundle', bundle.id),
      },
    ],
    data: toBundleCardData(
      bundle,
      allPrograms,
      allFeatures,
      allContent,
      submittedEditBundle,
    ),
  }));

  return [
    ...existingBundles,
    ...toBundleCardSubmittedItems(submittedNewBundle),
  ];
};

const toBundleCardSubmittedItems = (
  submittedNewBundle: NewBundleSubmit,
): Array<BundleCardItem> => {
  if (isEmpty(submittedNewBundle)) return [];

  return submittedNewBundle.map((i) => ({
    onClick: noop,
    menu: [],
    data: {
      type: 'submitting' as Submitting,
      title: i.title,
    },
  }));
};

const toBundleCardData = (
  bundle: RawOtpBundle,
  allPrograms: AllPrograms,
  allFeatures: AllFeatures,
  allContent: AllContent,
  submittedEditBundle: EditBundleSubmit,
) => {
  if (submittedEditBundle.some((b) => b.id === bundle.id)) {
    return {
      type: 'submitting' as Submitting,
      title: bundle.title,
    };
  }

  return {
    type: 'standard' as Standard,
    title: bundle.title,
    price: bundle?.oneTimeProduct?.price
      ? `$ ${bundle?.oneTimeProduct?.price / 100}`
      : '',
    isFree: Boolean(bundle.isFree),
    details: toBudleDetails(bundle?.items),
    programs: toProgramDetails(allPrograms, bundle?.items),
    status: {
      label: bundle.status.toLowerCase(),
      color: statusColor[bundle.status],
    },
    content: toContentDetails(allContent, bundle?.items),
    features: toOTPBundleItemFeatures(allFeatures, bundle),
  };
};

const toOTPBundleItemFeatures = (
  allFeatures: AllFeatures,
  bundle?: RawOtpBundle,
) =>
  allFeatures.map((f) => ({
    id: f.id,
    name: f.name,
    checked: !bundle
      ? false
      : !!(bundle?.items || []).find(
          (i) => i.itemType === BundleItemType.Feature && i.itemId === f.id,
        ),
  }));

type Cta = {
  label: string;
  onClick: () => void;
  color?: ButtonProps['color'];
  variant?: ButtonStylingProps['variant'];
  className?: string;
  disabled?: boolean;
  infoText?: string;
  tooltip?: string[];
};

export type FormCta = {
  header: Array<Cta>;
  footer: Array<Cta>;
};

export const toFormCta = ({
  editMode,
  dispatch,
  landingPagePath,
  readonly,
  bundleId,
  published,
  submitting,
  unsavedChanges,
  wasPurchased,
  isFree,
}: {
  editMode: boolean;
  dispatch: EditOtpBundleVMDispatcher;
  landingPagePath: string;
  readonly: boolean;
  bundleId: number | null;
  published: boolean;
  submitting: boolean;
  unsavedChanges: boolean;
  wasPurchased: boolean;
  isFree: boolean;
}): FormCta => ({
  header:
    readonly && !isFree
      ? [
          {
            label: 'Duplicate',
            onClick: () => bundleId && dispatch('duplicateBundle', bundleId),
          },
        ]
      : readonly && isFree
        ? [
            {
              label: 'Save',
              onClick: () => dispatch('saveBundle'),
              color: 'primaryTonal',
              disabled: submitting,
            },
            {
              label: 'Duplicate',
              onClick: () => bundleId && dispatch('duplicateBundle', bundleId),
            },
          ]
        : published
          ? [
              {
                label: 'Save',
                onClick: () => dispatch('saveBundle'),
                color: 'primaryTonal',
                disabled: submitting,
              },
            ]
          : [
              {
                label: editMode ? 'Save' : 'Save as draft',
                onClick: () => dispatch('saveBundle'),
                color: 'primaryTonal',
                disabled: submitting,
              },
              {
                label: 'Publish',
                onClick: () => dispatch('publishBundle'),
                disabled: submitting,
              },
            ],
  footer: readonly
    ? []
    : editMode
      ? [
          {
            label: 'Edit landing page',
            onClick: () =>
              unsavedChanges
                ? dispatch('confirmRedirect', landingPagePath)
                : dispatch('redirect', landingPagePath),
            color: 'primary',
            variant: 'text',
            className: 'w-max',
            disabled: submitting,
          },
          {
            label: 'Archive',
            onClick: () => dispatch('archiveBundle'),
            color: 'neutral',
            variant: 'text',
            className: 'w-full',
            disabled: submitting,
          },
          {
            label: 'Delete',
            onClick: () => dispatch('deleteBundle'),
            color: 'errorTonal',
            disabled: !bundleId || wasPurchased || submitting,
            className: 'w-full',
          },
        ]
      : [
          {
            label: 'Edit landing page',
            onClick: noop,
            color: 'primary',
            variant: 'text',
            className: 'w-max',
            disabled: true,
            infoText:
              "You'll be able to edit the landing page once you've created and saved your offer",
          },
        ],
});
export const toFormProgramsEmptyState = (
  dispatch: EditOtpBundleVMDispatcher,
  unsavedChanges: boolean,
  otpPath: string,
) => ({
  urlLabel: 'Create your first program',
  label: 'in order to be able to add them to your bundle',
  onClick: () =>
    unsavedChanges
      ? dispatch('confirmRedirect', otpPath)
      : dispatch('redirect', otpPath),
});

export const toFormContentEmptyState = (
  dispatch: EditOtpBundleVMDispatcher,
  unsavedChanges: boolean,
  extrasPath: string,
) => ({
  urlLabel: 'Create your first content item',
  label: 'in order to be able to add them to your bundle',
  onClick: () =>
    unsavedChanges
      ? dispatch('confirmRedirect', extrasPath)
      : dispatch('redirect', extrasPath),
});

export type ListFilter = {
  label: string;
  value: string;
  badge: number;
};

export const toListFilterTabs = (
  bundles: RawOtpBundle[],
): Array<ListFilter> => [
  {
    label: 'All',
    value: 'All',
    badge: bundles.length,
  },
  {
    label: 'Drafts',
    value: 'Drafts',
    badge: bundles.filter((b) => b.status === BundleStatus.Draft).length,
  },
  {
    label: 'Published',
    value: 'Published',
    badge: bundles.filter((b) => b.status === BundleStatus.Published).length,
  },
  {
    label: 'Archived',
    value: 'Archived',
    badge: bundles.filter((b) => b.status === BundleStatus.Archived).length,
  },
  {
    label: 'Free',
    value: 'Free',
    badge: bundles.filter((b) => b.isFree).length,
  },
];

export const getOfferLinks = (
  dispatch: BundlesVMDispatcher,
  bundle?: RawOtpBundle,
) => {
  if (!bundle?.id) {
    return [];
  }

  const isFree = bundle.checkoutPage.type === CheckoutPageType.FreeOtpBundle;

  return [
    ...(isFree
      ? [
          {
            ...toCollectionLink('redeemOfferLink'),
            description: (
              <>
                The link for your <em>any</em> user to Redeem an Offer. The
                fastest way to collect the email and give them access.
              </>
            ),
            url: bundle.checkoutPage.url,
            copy: () => dispatch('copyToClipboard', bundle.checkoutPage.url),
            recommended: true,
          },
          {
            ...toCollectionLink('offerLandingPage'),
            url: bundle.landingPageUrl,
            copy: () => dispatch('copyToClipboard', bundle.landingPageUrl),
            edit: () => dispatch('editLandingPage', bundle.id),
          },
        ]
      : [
          {
            ...toCollectionLink('offerLandingPage'),
            url: bundle.landingPageUrl,
            copy: () => dispatch('copyToClipboard', bundle.landingPageUrl),
            edit: () => dispatch('editLandingPage', bundle.id),
          },
          {
            ...toCollectionLink('offerCheckoutPage'),
            url: bundle.checkoutPage.url,
            copy: () => dispatch('copyToClipboard', bundle.checkoutPage.url),
          },
        ]),
    {
      ...toCollectionLink('offerDeepLink'),
      url: bundle.deeplinkUrl,
      copy: () => dispatch('copyToClipboard', bundle.deeplinkUrl),
    },
  ].filter((l) => l.url);
};

const toCollectionLink = (
  type: keyof typeof collectionLinks,
): { title: string; description: string } => {
  const link = collectionLinks[type];
  return {
    title: link.title,
    description: link.description,
  };
};

const MIN_PRICE_IN_CENTS = 99;
const MAX_PRICE_IN_CENTS = 100000;

export const offerPriceTransformation = (value: string) => {
  if (value === '') return value;
  const numberValue = Number(value) * 100;
  if (numberValue < MIN_PRICE_IN_CENTS) return `${MIN_PRICE_IN_CENTS / 100}`;
  if (numberValue > MAX_PRICE_IN_CENTS) return `${MAX_PRICE_IN_CENTS / 100}`;
  return value;
};

export const toFormOnChangeArray = <T,>(newValue: T, selectedValues: T[]) => {
  const hasItem = selectedValues.some((value) => value === newValue);
  return hasItem
    ? selectedValues.filter((value) => value !== newValue)
    : [...selectedValues, newValue];
};

export const trackRedirectEvent = ({
  path,
  landingPagePath,
  showDiscountsPath,
  createDiscountPath,
  bundleId,
}: {
  path: string;
  landingPagePath: string;
  showDiscountsPath: string;
  createDiscountPath: string;
  bundleId: number | null;
}) => {
  if (path === landingPagePath) {
    trackEvent({
      type: 'Offer landing page edited',
      payload: {
        offerId: bundleId || 0,
      },
    });
  }
  if (path === showDiscountsPath) {
    trackEvent({
      type: 'Redirected to offer existing discounts',
      payload: {
        offerId: bundleId || 0,
      },
    });
  }
  if (path === createDiscountPath) {
    trackEvent({
      type: 'Redirected to offer new discount',
      payload: {
        offerId: bundleId || 0,
      },
    });
  }
};
